import Breadcrumb, { Crumb, Separator } from "@asphalt-react/breadcrumb";
import Button from "@asphalt-react/button";
import Dropdown from "@asphalt-react/dropdown";
import Flag from "@asphalt-react/flag";
import Stack from "@asphalt-react/stack";
import TextField from "@asphalt-react/textfield";
import React, { useContext, useEffect, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import AnimatedAssetSelector from "../../common/components/AnimatedAssetSelector";
import { UserContext } from "../../common/hooks/UserContext";
import routes from "../../common/models/route-constants";
import LoadingPage from "../../common/pages/LoadingPage";
import { arrayOfStringsToArrayOfIdKeyObjects } from "../../common/utils/generalUtils";
import {
  createAnimatedIllustration,
  getAnimatedIllustrationNameComponentCategories,
  getCombinedName,
} from "../api/animatedIllustrationsApi";
import {
  getAnimatedIllustrationNameError,
  validAnimatedIllustrationName,
} from "../validations/animatedIllustrationValidations";
import styles from "./CreateAnimatedIllustrationPage.module.css";

function CreateAnimatedIllustrationPage() {
  const content =
    "1. All lowercase characters\n2. No special characters except '_'\n3. No numbers";

  let history = useHistory();
  const location = useLocation();

  // Context state
  const { user } = useContext(UserContext);

  // page loading state
  const [isLoading, setIsLoading] = useState(false);

  // Input value states
  const [productCategories, setProductCategories] = useState([]);
  const [animatedIllustrationCategories, setAnimatedIllustrationCategories] =
    useState([]);
  const [selectedProductCategory, setSelectedProductCategory] = useState("");
  const [
    selectedAnimatedIllustrationCategory,
    setSelectedAnimatedIllustrationCategory,
  ] = useState("");
  const [animatedIllustrationName, setAnimatedIllustrationName] =
    useState(null);
  const [animatedIllustrationFile, setAnimatedIllustrationFile] =
    useState(null);

  // Error states
  const [productCategoryDropdownError, setProductCategoryDropdownError] =
    useState(null);
  const [
    animatedIllustrationCategoryDropdownError,
    setAnimatedIllustrationCategoryDropdownError,
  ] = useState(null);
  const [animatedIllustrationNameError, setAnimatedIllustrationNameError] =
    useState(null);
  const [assetSelectorError, setAssetSelectorError] = useState(null);

  function buildNotification(animatedAssetId, name) {
    return {
      title: "Animated Illustration added",
      content: `${getCombinedName(name)} has been added successfully`,
      ctaText: "View",
      animatedIllustrationId: animatedAssetId,
    };
  }

  // Cancel button click
  const handeCancelClick = () => {
    //TODO : Check for any changes and give user a prompt
    history.goBack();
  };

  // Save Button click
  const handleSaveClick = async () => {
    // Check if AnimatedIllustration Category is selected
    if (selectedProductCategory) {
      setProductCategoryDropdownError(null);
    } else {
      setProductCategoryDropdownError(
        "AnimatedIllustration category cannot be empty"
      );
    }

    // Check if AnimatedIllustration Size is selected
    if (selectedAnimatedIllustrationCategory) {
      setAnimatedIllustrationCategoryDropdownError(null);
    } else {
      setAnimatedIllustrationCategoryDropdownError(
        "AnimatedIllustration size cannot be empty"
      );
    }

    // Check if animatedIllustration name passes validations
    if (validAnimatedIllustrationName(animatedIllustrationName)) {
      setAnimatedIllustrationNameError(null);
    } else {
      setAnimatedIllustrationNameError(
        getAnimatedIllustrationNameError(animatedIllustrationName)
      );
    }

    //Check if animatedIllustration file is selected
    if (animatedIllustrationFile) {
      setAssetSelectorError(null);
    } else {
      setAssetSelectorError("AnimatedIllustration file cannot be empty");
    }

    if (
      selectedProductCategory &&
      selectedAnimatedIllustrationCategory &&
      validAnimatedIllustrationName(animatedIllustrationName) &&
      animatedIllustrationFile
    ) {
      setIsLoading(true);

      var name = {
        product_category: selectedProductCategory.key,
        animated_illustration_category:
          selectedAnimatedIllustrationCategory.key,
        animated_illustration_name: animatedIllustrationName,
      };

      try {
        const animatedIllustrationId = await createAnimatedIllustration(
          name,
          animatedIllustrationFile,
          user.uid
        );
        setIsLoading(false);
        history.push(`/${routes.ANIMATED_ASSETS}/${routes.ILLUSTRATIONS}`, {
          notification: buildNotification(animatedIllustrationId, name),
        });
      } catch (error) {
        setIsLoading(false);
        if (error.name === "FirebaseError") {
          console.log(error.message);
        } else if (
          error.message ===
          "AnimatedIllustration with the same name already exists"
        ) {
          setAnimatedIllustrationNameError(error.message);
        } else {
          console.log(error);
        }
      }
    }
  };

  useEffect(() => {
    getAnimatedIllustrationNameComponentCategories()
      .then((querySnapshot) => {
        querySnapshot.forEach(function (doc) {
          if (doc.id === "product_category") {
            setProductCategories(
              arrayOfStringsToArrayOfIdKeyObjects(doc.data().values)
            );
          }
          if (doc.id === "animated_illustration_category") {
            setAnimatedIllustrationCategories(
              arrayOfStringsToArrayOfIdKeyObjects(doc.data().values)
            );
          }
        });
      })
      .catch((error) => {
        console.error(error);
      });
  }, [setProductCategories, setAnimatedIllustrationCategories, location]);

  return (
    <React.Fragment>
      {isLoading ? (
        <div className={styles.loadingContainer}>
          <LoadingPage />
        </div>
      ) : null}
      <div className={styles.createPageContainer}>
        <div className={styles.breadcrumbContainer}>
          <Breadcrumb>
            <Crumb
              text="animated illustrations"
              tagProps={{
                to: `/${routes.ANIMATED_ASSETS}/${routes.ILLUSTRATIONS}`,
              }}
              as={Link}
            />
            <Separator />
            <Crumb
              text="add animated illustration"
              tagProps={{
                to: `/${routes.ANIMATED_ASSETS}/${routes.ILLUSTRATIONS}/${routes.CREATE}`,
              }}
              as={Link}
            />
          </Breadcrumb>
        </div>
        <div>
          <h3 className={styles.heading}>Add Animated Illustration</h3>
          <div className={styles.flagContainer}>
            <Flag
              page
              info
              title="How to name your Animated Illustration"
              content={content}
            />
          </div>
          <div className={styles.formRow}>
            <div className={styles.productCategoryContainer}>
              <p className={styles.inputLabel}>Product Category</p>
              <div className={styles.inputContainer}>
                <Dropdown
                  typeahead
                  items={productCategories}
                  onChange={(selectedProductCategory) => {
                    setSelectedProductCategory(selectedProductCategory);
                    setProductCategoryDropdownError(null);
                  }}
                  stretch
                  invalid={!!productCategoryDropdownError}
                />
              </div>
              {productCategoryDropdownError ? (
                <Flag error title={productCategoryDropdownError} />
              ) : null}
            </div>
            <div className={styles.animatedIllustrationCategoryContainer}>
              <p className={styles.inputLabel}>
                Animated Illustration Category
              </p>
              <div className={styles.inputContainer}>
                <Dropdown
                  items={animatedIllustrationCategories}
                  onChange={(selectedAnimatedAssetSize) => {
                    setSelectedAnimatedIllustrationCategory(
                      selectedAnimatedAssetSize
                    );
                    setAnimatedIllustrationCategoryDropdownError(null);
                  }}
                  stretch
                  invalid={!!animatedIllustrationCategoryDropdownError}
                />
              </div>
              {animatedIllustrationCategoryDropdownError ? (
                <Flag error title={animatedIllustrationCategoryDropdownError} />
              ) : null}
            </div>
            <div className={styles.animatedIllustrationNameContainer}>
              <p className={styles.inputLabel}>Animated Illustration Name</p>
              <div className={styles.inputContainer}>
                <TextField
                  required={!!animatedIllustrationNameError}
                  size="m"
                  stretch
                  placeholder="AnimatedAsset Name"
                  type="text"
                  invalid={!!animatedIllustrationNameError}
                  onChange={(e) => {
                    setAnimatedIllustrationName(e.target.value);
                    setAnimatedIllustrationNameError(null);
                  }}
                />
              </div>
              {animatedIllustrationNameError ? (
                <Flag error title={animatedIllustrationNameError} />
              ) : null}
            </div>
          </div>

          <div className={styles.assetSelectorContainer}>
            <div className={styles.inputContainer}>
              <AnimatedAssetSelector
                heading="Animated Illustration File"
                onAssetSelected={(file) => {
                  if (file) {
                    setAnimatedIllustrationFile(file);
                    setAssetSelectorError(null);
                  }
                }}
              />
            </div>
            {assetSelectorError ? (
              <Flag error title={assetSelectorError} />
            ) : null}
          </div>
          <Stack>
            <Button primary onClick={handleSaveClick}>
              Save
            </Button>
            <Button secondary onClick={handeCancelClick}>
              Cancel
            </Button>
          </Stack>
        </div>
      </div>
    </React.Fragment>
  );
}

export default CreateAnimatedIllustrationPage;
